import { useState } from 'react';
import { formatDatePT } from '../utils/dateFormat';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Calendar, Clock, Euro, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DetailedReport = ({ open, onClose, userData, entries, weekStart, weekEnd }) => {
  const { t } = useTranslation();
  
  if (!userData || !entries) return null;

  // Agrupar por data
  const entriesByDate = {};
  entries.forEach(entry => {
    if (!entriesByDate[entry.date]) {
      entriesByDate[entry.date] = [];
    }
    entriesByDate[entry.date].push(entry);
  });

  const totalHours = entries.reduce((sum, e) => sum + (e.hours_worked || 0), 0);
  const totalAmount = totalHours * (userData.hourly_rate || 0);

  const handleDownload = () => {
    const dates = Object.keys(entriesByDate).sort();
    let reportText = `RELATÓRIO DETALHADO - ${userData.name}\n`;
    reportText += `Período: ${formatDatePT(weekStart)} - ${formatDatePT(weekEnd)}\n\n`;
    
    dates.forEach(date => {
      const dayEntries = entriesByDate[date];
      const dayHours = dayEntries.reduce((sum, e) => sum + e.hours_worked, 0);
      reportText += `\n${formatDatePT(date)} - ${dayHours.toFixed(2)}h\n`;
      dayEntries.forEach(entry => {
        reportText += `  ${entry.start_time} - ${entry.end_time} | ${entry.client} | ${entry.hours_worked.toFixed(2)}h\n`;
      });
    });
    
    reportText += `\n\nTOTAL: ${totalHours.toFixed(2)}h\n`;
    reportText += `VALOR: €${totalAmount.toFixed(2)}\n`;
    
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_${userData.name}_${weekStart}_${weekEnd}.txt`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t('detailedReport')} - {userData.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Sumário */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">{t('totalHours')}</p>
                    <p className="text-2xl font-bold text-gray-800">{totalHours.toFixed(1)}h</p>
                  </div>
                  <Clock className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-600 to-green-700 border-0">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">{t('totalAmount')}</p>
                    <p className="text-2xl font-bold text-gray-800">€{totalAmount.toFixed(2)}</p>
                  </div>
                  <Euro className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">{t('workDays')}</p>
                    <p className="text-2xl font-bold text-gray-800">{Object.keys(entriesByDate).length}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detalhes por dia */}
          <div className="space-y-3">
            {Object.keys(entriesByDate).sort().map(date => {
              const dayEntries = entriesByDate[date];
              const dayHours = dayEntries.reduce((sum, e) => sum + e.hours_worked, 0);
              
              return (
                <Card key={date} className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-gray-800 text-lg">
                        {new Date(date + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long' })}
                      </CardTitle>
                      <span className="text-[#3da5a5] font-bold text-xl">{dayHours.toFixed(1)}h</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dayEntries.map((entry, idx) => (
                        <div key={idx} className="flex justify-between items-start p-3 bg-gray-50 rounded">
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium">{entry.client}</p>
                            <p className="text-gray-400 text-sm">{entry.project}</p>
                            {entry.description && <p className="text-gray-300 text-xs mt-1">{entry.description}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-gray-800 text-sm">{entry.start_time} - {entry.end_time}</p>
                            <p className="text-[#3da5a5] font-bold">{entry.hours_worked.toFixed(1)}h</p>
                            {entry.lunch_hours > 0 && (
                              <p className="text-orange-400 text-xs">Almoço: -{entry.lunch_hours}h</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Button onClick={handleDownload} className="w-full bg-[#3da5a5] hover:bg-[#2d8585]">
            <Download className="w-4 h-4 mr-2" />
            {t('downloadReport')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DetailedReport;